import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  OnChanges,
  forwardRef, // Required for provider
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
  FormGroup,
  AbstractControl
} from '@angular/forms'; // Required for forms
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  of,
  tap,
  takeUntil,
  Subscription,
  merge,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { SchemaApiService } from '../../../core/services/schema-api.service';
import { MatDialog } from '@angular/material/dialog';
import { SwAdvancedLookupDialogComponent } from '../../../feature/transentityform/sw-advanceFilter/sw-advanced-lookup-dialog.component';

@Component({
  selector: 'sw-dynamic-autocomplete',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sw-autocomplete.component.html',
  styleUrl: './sw-autocomplete.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SWDynamicAutocompleteComponent),
      multi: true,
    },
  ],
})
export class SWDynamicAutocompleteComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  // @Input() config: any; // Add this input to read dataSource.filter
  // In your class properties:
  @Input() config: any = {};
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() api: string = '';
  @Input() valueField: string = 'id';
  @Input() labelField: string = 'name';
  @Input() minLength: number = 2;
  @Output() optionSelected = new EventEmitter<any>();

  // 1. ADD THIS NEW INPUT
  @Input() displayLabel: string = '';
  @Input() dependsOn: string[] | string | Record<string, string> | null = [];
  @Input() parentForm!: FormGroup;

  // Internal state
  @Input() ngModel: any;
  @Output() ngModelChange = new EventEmitter<any>();

  isOpen = false;
  isLoading = false;
  searchText: string = '';
  items: any[] = [];
  activeIndex = -1;
  disabled = false;

  private search$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  // ControlValueAccessor Callbacks
  private onChange: (val: any) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(
    private apiSvc: SchemaApiService,
    private el: ElementRef,
    private dialog: MatDialog
  ) { }


  // NEW: Looking at the root level
  // get isAdvancedMode(): boolean {
  //   // Check if config.filter exists and is true
  //   return this.config.filter === true;
  // }

  // sw-autocomplete.component.ts

get isAdvancedMode(): boolean {
  // Use optional chaining (?.) to prevent the "undefined" error
  return this.config?.filter === true;
}


  get isInputReadOnly(): boolean {
  // Return true ONLY if you want to block typing. 
  // Since you want "old autocomplete working", we return false.
  return false; 
}

handleInputInteraction(): void {
  if (this.disabled) return;
  this.isOpen = !this.isOpen; // Toggle dropdown
  if (this.isOpen) this.triggerSearchOnClick();
}

handleFilterClick(): void {
  if (this.disabled) return;
  this.openAdvancedDialog(); // Opens the SwAdvancedLookupDialogComponent
}
  // Triggered when user clicks the input or the magnifying glass
  handleInteraction(): void {
    if (this.disabled) return;

    if (this.isAdvancedMode) {
       this.triggerSearchOnClick();
      this.openAdvancedDialog();
    } else {
      this.triggerSearchOnClick();
    }
  }
  openAdvancedDialog() {
    const dialogRef = this.dialog.open(SwAdvancedLookupDialogComponent, {
      width: this.config.dataSource?.dialogWidth || '800px',
      disableClose: true,
      data: {
        config: this.config,
        filters: this.getCurrentFilters()
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.selectItem(result);
      }
    });
  }
  private getCurrentFilters(): any {
    const filters: any = {};
    const deps = this.getDependsArray();
    if (deps.length && this.parentForm) {
      deps.forEach(d => {
        filters[d] = this.parentForm.get(d)?.value;
      });
    }
    return filters;
  }

  // --- ControlValueAccessor Implementation ---

  // writeValue(value: any): void {
  //   this.ngModel = value;
  //   if (value && this.api) {
  //     // Logic to fetch label if value (ID) is provided by parent
  //     this.apiSvc.getById(this.api, value).subscribe((res) => {
  //       this.searchText = res?.[this.labelField] ?? '';
  //     });
  //   } else if (!value) {
  //     this.searchText = '';
  //   }
  // }

  writeValue(value: any): void {
    if (!value) {
      this.searchText = '';
      return;
    }

    // If object → we already have label
    if (typeof value === 'object') {
      this.searchText = value.label || '';
      this.ngModel = value.id;
      return;
    }

    // Fallback → API lookup
    if (this.api) {
      this.apiSvc.getById(this.api, value).subscribe(res => {
        this.searchText = res?.[this.labelField] ?? '';
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Logic ---

  ngOnInit(): void {
    console.log('Current Field Config:', this.config);
    console.log('Is Advanced Mode:', this.isAdvancedMode);
    // ⭐ IMPORTANT — evaluate immediately on load
    this.updateDependencyState();

    if (this.getDependsArray().length && this.parentForm) {

      this.getDependsArray().forEach(dep => {

        const parentCtrl = this.parentForm.get(dep);
        if (!parentCtrl) return;

        parentCtrl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {

            this.ngModel = null;
            this.searchText = '';
            this.onChange(null);

            // reevaluate enable/disable
            this.updateDependencyState();
          });
      });
    }

    this.search$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          this.isLoading = true;
          this.activeIndex = -1;
        }),
        switchMap((query) => {
          if (!query || query.length < this.minLength || !this.api)
            return of([]);

          // return this.apiSvc.lookup(this.api, query);
          const filters: any = {};

          this.getDependsArray().forEach(dep => {
            const val = this.parentForm?.get(dep)?.value;
            if (val) filters[dep] = val;
          });

          return this.apiSvc.lookup(this.api, query, filters);
        }),
        tap(() => (this.isLoading = false)),
      )
      .subscribe((res) => {
        this.items = Array.isArray(res) ? res : [];
        this.isOpen = true;
      });
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   // Handling external ngModel changes if used alongside Reactive Forms
  //   if (changes['ngModel'] && !changes['ngModel'].firstChange) {
  //     this.writeValue(changes['ngModel'].currentValue);
  //   }
  // }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['displayLabel']?.currentValue) {
      this.searchText = changes['displayLabel'].currentValue;
    }

    if (changes['ngModel'] && !changes['ngModel'].firstChange) {
      this.writeValue(changes['ngModel'].currentValue);
    }

    // ⭐ VERY IMPORTANT
    this.setupDependsOnWatcher();
  }

  onInputChange(value: string): void {
    // if (this.isAdvancedMode) return; // Prevent typing in advanced mode
    this.searchText = value;
    this.onChange(null); // Clear form value while user is typing
    this.ngModelChange.emit(null);

    const txt = value.trim();
    if (txt.length < this.minLength) {
      this.items = [];
      this.isOpen = false;
      return;
    }
    this.isOpen = true;
    this.search$.next(txt);
  }

  // selectItem(item: any): void {
  //   const selectedId = item?.[this.valueField];
  //   const selectedLabel = item?.[this.labelField];

  //   this.searchText = selectedLabel ?? '';
  //   this.ngModel = selectedId;

  //   // Notify Reactive Form and Template-driven form
  //   this.onChange(selectedId);
  //   this.onTouched();
  //   this.ngModelChange.emit(selectedId);
  //   // 🔥 Emit full object for parent logic
  //   this.optionSelected.emit(item);

  //   this.close();
  // }
  selectItem(item: any): void {
    console.log(
      'AUTOCOMPLETE SELECT',
      this.config?.name,
      item
    );
    const selectedId = item?.[this.valueField];
    const selectedLabel = item?.[this.labelField];

    const payload = {
      id: selectedId,
      label: selectedLabel
    };

    this.searchText = selectedLabel ?? '';
    this.ngModel = payload;
    console.log(
      'NGMODEL BEFORE',
      this.ngModel
    );
    this.onChange(payload);
    console.log(
      'NGMODEL AFTER',
      payload
    );
    this.ngModelChange.emit(payload);
    this.optionSelected.emit(item);
    this.close();
  }

  // --- UI Helpers ---

  triggerSearchOnClick(): void {
    const txt = (this.searchText ?? '').trim();
    this.isOpen = true;
    this.search$.next(txt);
  }

  onFocus(): void {
    if (
      this.searchText.trim().length >= this.minLength &&
      this.items.length > 0
    ) {
      this.isOpen = true;
    }
  }

  close(): void {
    this.isOpen = false;
    this.activeIndex = -1;
  }

  onKeyDown(e: KeyboardEvent): void {
    if (!this.isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.items.length - 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    } else if (e.key === 'Enter') {
      if (this.activeIndex >= 0 && this.items[this.activeIndex]) {
        e.preventDefault();
        this.selectItem(this.items[this.activeIndex]);
      }
    } else if (e.key === 'Escape') {
      this.close();
    }
  }

  trackByFn(index: number, item: any): any {
    return item?.[this.valueField] ?? index;
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  ngOnDestroy(): void {
    this.dependsSub?.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateDependencyState(): void {

    const deps = this.getDependsArray();

    // no dependencies → always enabled
    if (!deps.length || !this.parentForm) {
      this.disabled = false;
      return;
    }

    const allSelected = deps.every(dep => {

      const v = this.parentForm.get(dep)?.value;

      // handles object {id,label}
      if (v && typeof v === 'object') {
        return !!(v.id || v.value);
      }

      return !!v;
    });

    this.disabled = !allSelected;

    // if disabled → clear safely
    if (this.disabled) {
      this.items = [];
      this.isOpen = false;
      this.searchText = '';
      this.onChange(null);
    }
  }

  getDependencyMessage(): string {

    if (!this.dependsOn?.length) return '';

    const missing = this.getDependsArray().find(dep =>
      !this.parentForm.get(dep)?.value
    );

    if (!missing) return '';

    return `Select ${missing.replace('_id', '')} first`;
  }

  private getDependsArray(): string[] {

    if (!this.dependsOn) return [];

    if (Array.isArray(this.dependsOn))
      return this.dependsOn as string[];

    if (typeof this.dependsOn === 'string')
      return [this.dependsOn];

    if (typeof this.dependsOn === 'object')
      return Object.values(this.dependsOn) as string[];

    return [];
  }

  private dependsSub?: Subscription;

  private setupDependsOnWatcher(): void {

    this.dependsSub?.unsubscribe();

    if (!this.parentForm) return;

    const depends = this.getDependsArray();
    if (!depends.length) return;

    const controls: AbstractControl[] = depends
      .map((d: string) => this.parentForm!.get(d))
      .filter((c): c is AbstractControl => !!c);

    if (!controls.length) return;

    this.dependsSub = merge(
      ...controls.map((c: AbstractControl) => c.valueChanges)
    ).subscribe(() => {

      const missing = controls.some(
        (c: AbstractControl) => !c.value
      );

      if (missing && this.ngModel) {

        this.items = [];
        this.isOpen = false;
        this.searchText = '';

        // ⭐ CORRECT CVA UPDATE
        this.onChange(null);
        this.ngModelChange.emit(null);
      }

      this.updateDependencyState();
    });

    // initial state
    this.updateDependencyState();
  }

}
